/**
 * Login Form Component
 */

import React, { useState } from 'react';
import { Eye, EyeOff, AlertCircle, Mail, Lock } from 'lucide-react';
import { useSignIn, useOAuthSignIn } from '../../auth/authHooks';
import './LoginForm.css';

interface LoginFormProps {
    onSwitchToForgotPassword?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
    onSwitchToForgotPassword
}) => {
    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    const { signIn, isLoading, error } = useSignIn();
    const { signInWithGoogle } = useOAuthSignIn();

    // Form validation
    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        if (!formData.username.trim()) {
            errors.username = 'Username is required';
        }

        if (!formData.password) {
            errors.password = 'Password is required';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const result = await signIn(formData.username, formData.password);
        if (!result.success) {
            // Check if this is a new password challenge
            if (result.error?.message?.includes('CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED')) {
                // The challenge will be detected by the LoginPage useEffect
                return;
            }
            // Error is handled by the hook
            console.error('Sign in failed:', result.error);
        }
    };

    // Handle input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));

        // Clear validation error when user starts typing
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: '',
            }));
        }
    };

    // Handle Google sign in
    const handleGoogleSignIn = () => {
        signInWithGoogle();
    };

    return (
        <div className="login-form">
            <div className="form-header">
                <h2>Welcome Back</h2>
                <p>Sign in to your account to continue</p>
            </div>

            <form onSubmit={handleSubmit} className="form">
                {/* Username/Email Field */}
                <div className="form-group">
                    <label htmlFor="username" className="form-label">
                        Username or Email
                    </label>
                    <div className="input-container">
                        <Mail className="input-icon" size={20} />
                        <input
                            type="text"
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                            className={`form-input with-icon ${validationErrors.username ? 'error' : ''}`}
                            placeholder="Enter your username or email"
                            disabled={isLoading}
                        />
                    </div>
                    {validationErrors.username && (
                        <span className="form-error-message">
                            <AlertCircle size={16} />
                            {validationErrors.username}
                        </span>
                    )}
                </div>

                {/* Password Field */}
                <div className="form-group">
                    <label htmlFor="password" className="form-label">
                        Password
                    </label>
                    <div className="input-container">
                        <Lock className="input-icon" size={20} />
                        <input
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            className={`form-input with-icon ${validationErrors.password ? 'error' : ''}`}
                            placeholder="Enter your password"
                            disabled={isLoading}
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={isLoading}
                        >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                    </div>
                    {validationErrors.password && (
                        <span className="form-error-message">
                            <AlertCircle size={16} />
                            {validationErrors.password}
                        </span>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div className="error-banner">
                        <AlertCircle size={20} />
                        <span>{error.message}</span>
                    </div>
                )}

                {/* Submit Button */}
                <button
                    type="submit"
                    className="btn btn-primary btn-full"
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            <div className="spinner" />
                            Signing In...
                        </>
                    ) : (
                        'Sign In'
                    )}
                </button>

                {/* Forgot Password Link */}
                <div className="form-footer">
                    <button
                        type="button"
                        className="link-button"
                        onClick={onSwitchToForgotPassword}
                        disabled={isLoading}
                    >
                        Forgot your password?
                    </button>
                </div>
            </form>

            {/* Divider */}
            <div className="divider">
                <span>or continue with</span>
            </div>

            {/* OAuth Buttons */}
            <div className="oauth-buttons">
                <button
                    type="button"
                    className="oauth-button google"
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                >
                    <svg className="oauth-icon" viewBox="0 0 24 24">
                        <path
                            fill="#4285F4"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                            fill="#34A853"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                            fill="#FBBC05"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                            fill="#EA4335"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                    </svg>
                    Continue with Google
                </button>

            </div>

            {/* Contact Admin Message */}
            <div className="signup-link">
                <span>Don't have an account? </span>
                <span className="contact-admin">
                    Contact your administrator to create an account
                </span>
            </div>
        </div>
    );
};

export default LoginForm;
